/**
 * Stickers that go on faces: an emoji laid on each face a photo has, sized
 * to it and turned with it. Once laid they are ordinary stickers (SnapSticker),
 * to be moved, pinched or taken off like any other -- so the server checks
 * them as it checks every emoji, and nothing new is stored.
 *
 * Places are fractions of the picture, as every sticker's are. A sticker at
 * scale 1 is 14% of the picture's width (.snap-sticker, 14cqw), which is what
 * turns a size measured off a face into a scale.
 */
import { DECOR_MAX_SCALE, DECOR_MIN_SCALE, foldTurn } from './snapDecor';
import type { StickerArt } from './stickerArt';
import type { CaptionAt, SnapSticker } from './types';

export interface FacePoint {
  x: number;
  y: number;
}
export interface FaceMarks {
  leftEye: FacePoint;
  rightEye: FacePoint;
  nose: FacePoint;
  mouth: FacePoint;
}

/** How wide a sticker is at scale 1, as a fraction of the picture's width. */
export const STICKER_WIDTH_AT_ONE = 0.14;

type Anchor = 'eyes' | 'eachEye' | 'head' | 'nose' | 'lip' | 'mouth' | 'face';

/** What a piece is: an emoji, or a drawing (shared/stickerArt.ts). */
export type Piece = { emoji: string } | { art: StickerArt };

/** One piece of a look, and where on the face it goes. */
export interface Part {
  piece: Piece;
  anchor: Anchor;
  /** How wide, in eye-spans (the distance between the eyes). */
  width: number;
  /** How far up the face from its anchor, in eye-spans; down if less than nought. */
  lift?: number;
}

export interface FaceLook {
  name: string;
  /** What the strip shows for it. */
  preview: Piece;
  parts: readonly Part[];
}

const emoji = (glyph: string): Piece => ({ emoji: glyph });
const art = (name: StickerArt): Piece => ({ art: name });

/*
 * The top of a head is about 1.3 eye-spans above the eyes, tried on the
 * family's own photos. A drawing that
 * sits on it is lifted by that and by however far its own middle is above
 * where it meets the head -- which is why the lifts below are not all 1.3.
 */
export const FACE_LOOKS: readonly FaceLook[] = [
  {
    name: 'Dog',
    preview: art('dog-ears'),
    parts: [
      { piece: art('dog-ears'), anchor: 'head', width: 3.6, lift: 0.75 },
      { piece: art('dog-nose'), anchor: 'nose', width: 1.1, lift: -0.2 },
    ],
  },
  {
    name: 'Cat',
    preview: art('cat-ears'),
    parts: [
      { piece: art('cat-ears'), anchor: 'head', width: 3.2, lift: 1.85 },
      { piece: art('cat-whiskers'), anchor: 'nose', width: 3.0, lift: -0.15 },
    ],
  },
  {
    name: 'Bunny',
    preview: art('bunny-ears'),
    parts: [
      { piece: art('bunny-ears'), anchor: 'head', width: 2.2, lift: 2.25 },
      { piece: art('bunny-nose'), anchor: 'nose', width: 0.9, lift: -0.08 },
    ],
  },
  { name: 'Flower crown', preview: art('flower-crown'), parts: [{ piece: art('flower-crown'), anchor: 'head', width: 3.2, lift: 1.3 }] },
  { name: 'Mustache', preview: art('mustache'), parts: [{ piece: art('mustache'), anchor: 'lip', width: 1.7 }] },
  { name: 'Aviators', preview: art('aviators'), parts: [{ piece: art('aviators'), anchor: 'eyes', width: 2.5, lift: -0.05 }] },
  { name: 'Party hat', preview: art('party-hat'), parts: [{ piece: art('party-hat'), anchor: 'head', width: 1.7, lift: 2.2 }] },
  { name: 'Halo', preview: art('halo'), parts: [{ piece: art('halo'), anchor: 'head', width: 2.6, lift: 2.1 }] },
  { name: 'Devil horns', preview: art('devil-horns'), parts: [{ piece: art('devil-horns'), anchor: 'head', width: 2.8, lift: 1.8 }] },
  { name: 'Shades', preview: emoji('🕶️'), parts: [{ piece: emoji('🕶️'), anchor: 'eyes', width: 2.3 }] },
  { name: 'Crown', preview: emoji('👑'), parts: [{ piece: emoji('👑'), anchor: 'head', width: 2.2, lift: 1.7 }] },
  { name: 'Top hat', preview: emoji('🎩'), parts: [{ piece: emoji('🎩'), anchor: 'head', width: 2.4, lift: 1.9 }] },
  { name: 'Heart eyes', preview: emoji('❤️'), parts: [{ piece: emoji('❤️'), anchor: 'eachEye', width: 0.9 }] },
  { name: 'Star eyes', preview: emoji('⭐'), parts: [{ piece: emoji('⭐'), anchor: 'eachEye', width: 0.9 }] },
  { name: 'Clown nose', preview: emoji('🔴'), parts: [{ piece: emoji('🔴'), anchor: 'nose', width: 0.6 }] },
  { name: 'Kiss', preview: emoji('💋'), parts: [{ piece: emoji('💋'), anchor: 'mouth', width: 1.1 }] },
];

const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));
const round = (value: number) => Math.round(value * 1000) / 1000;

/**
 * Where a look goes on one face, on a picture `ratio` wide for every 1 high.
 * Measured in the picture's widths across and heights down, so the turn and
 * the sizes come out right on a picture that is not square.
 */
export function lookOn(face: FaceMarks, look: FaceLook, ratio: number): SnapSticker[] {
  // Everything in picture-widths: a step down of `dy` heights is dy / ratio widths.
  const dx = face.rightEye.x - face.leftEye.x;
  const dy = (face.rightEye.y - face.leftEye.y) / ratio;
  const span = Math.hypot(dx, dy);
  if (!(span > 0)) return [];
  const rotate = round(foldTurn((Math.atan2(dy, dx) * 180) / Math.PI));
  // "Up" on the face, which leans with it: the eye line turned a quarter back.
  const up = { x: dy / span, y: -dx / span };
  const mid = { x: (face.leftEye.x + face.rightEye.x) / 2, y: (face.leftEye.y + face.rightEye.y) / 2 };
  const lip = {
    x: face.nose.x + (face.mouth.x - face.nose.x) * 0.4,
    y: face.nose.y + (face.mouth.y - face.nose.y) * 0.4,
  };

  return look.parts.flatMap((part) => {
    const scale = round(clamp((part.width * span) / STICKER_WIDTH_AT_ONE, DECOR_MIN_SCALE, DECOR_MAX_SCALE));
    const lift = (part.lift ?? 0) * span;
    /** The piece, at `from` moved up the face by its lift; y back into heights. */
    const one = (from: FacePoint): SnapSticker => {
      const at: CaptionAt = {
        x: round(from.x + up.x * lift),
        y: round(from.y + up.y * lift * ratio),
        rotate,
        scale,
      };
      return 'art' in part.piece ? { art: part.piece.art, at } : { emoji: part.piece.emoji, at };
    };
    switch (part.anchor) {
      case 'eyes':
      case 'head':
        return [one(mid)];
      case 'eachEye':
        return [one(face.leftEye), one(face.rightEye)];
      case 'nose':
        return [one(face.nose)];
      case 'lip':
        // Just under the nose: four tenths of the way down to the mouth.
        return [one(lip)];
      case 'mouth':
        return [one(face.mouth)];
      case 'face':
        return [one({ x: (mid.x + face.mouth.x) / 2, y: (mid.y + face.mouth.y) / 2 })];
    }
  });
}

/** A look on every face, as many as there is room for under `room` stickers. */
export function lookOnAll(faces: readonly FaceMarks[], look: FaceLook, ratio: number, room: number): SnapSticker[] {
  const laid: SnapSticker[] = [];
  for (const face of faces) {
    const these = lookOn(face, look, ratio);
    if (laid.length + these.length > room) break;
    laid.push(...these);
  }
  return laid;
}
